(() => {
  // Wait for x-popover to be defined, then register our subclass that
  // never locks scroll, never appends to body, and uses a fast fade animation.
  customElements.whenDefined("x-popover").then(() => {
    if (customElements.get("x-technical-detail-popover")) {
      return;
    }

    const XPopover = customElements.get("x-popover");

    class XTechnicalDetailPopover extends XPopover {
      get shouldLock() {
        return false;
      }

      get shouldAppendToBody() {
        return false;
      }

      get shouldTrapFocus() {
        return false;
      }

      createEnterAnimationControls() {
        this.style.opacity = '1';
        this.style.transform = 'none';
        return {
          finished: Promise.resolve(),
          finish: () => {}
        };
      }

      createLeaveAnimationControls() {
        this.style.opacity = '0';
        return {
          finished: Promise.resolve(),
          finish: () => {}
        };
      }
    }

    customElements.define("x-technical-detail-popover", XTechnicalDetailPopover);
  });

  const mobileQuery = window.matchMedia("(max-width: 999px)");
  const GUTTER = 20;
  const MOBILE_VERTICAL_OFFSET = 6;
  const DESKTOP_MIN_BEAK_EDGE = 16;
  const FIXED_TRIGGER_GAP = 14;
  const BEAK_SIZE = 12;
  const TECHNICAL_GROUP_SELECTOR = [
    '.product-info__block-group[data-group-type="feature-badge-list"]',
    '.product-info__block-group[data-group-type="technical-detail-list"]'
  ].join(", ");

  function escapeCss(value) {
    if (window.CSS && typeof window.CSS.escape === "function") {
      return window.CSS.escape(value);
    }

    return String(value).replace(/["\\]/g, "\\$&");
  }

  function findTrigger(popover) {
    const popoverId = popover?.id;

    if (!popoverId) {
      return null;
    }

    return document.querySelector(
      `.technical-detail__info-button[aria-controls="${escapeCss(popoverId)}"]`
    );
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function setMobilePosition(popover, trigger) {
    if (!popover || !trigger || !mobileQuery.matches) {
      return;
    }

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const cardWidth = viewportWidth - GUTTER * 2;

    const hostRect = popover.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();
    const contentEl =
      popover.shadowRoot?.querySelector('[part="content"]') || popover;
    const cardHeight = contentEl.getBoundingClientRect().height
      || popover.getBoundingClientRect().height
      || Math.min(viewportHeight - GUTTER * 2, 520);

    // All coordinates relative to the host element (position: relative).
    const left = GUTTER - hostRect.left;
    const top = (triggerRect.top - hostRect.top) - MOBILE_VERTICAL_OFFSET - cardHeight;

    popover.style.setProperty("--technical-popover-mobile-width", `${cardWidth}px`);
    popover.style.setProperty("--technical-popover-mobile-left", `${left}px`);
    popover.style.setProperty("--technical-popover-mobile-top", `${top}px`);
  }

  function setDesktopPosition(popover, trigger) {
    if (!popover || !trigger || mobileQuery.matches) {
      return;
    }

    const viewportHeight = window.innerHeight;
    const detailRow = popover.closest(".technical-detail");

    if (!detailRow) {
      return;
    }

    const triggerRect = trigger.getBoundingClientRect();
    const triggerCenter = triggerRect.top + triggerRect.height / 2;

    const contentEl =
      popover.shadowRoot?.querySelector('[part="content"]') || popover;
    const popoverHeight = contentEl.getBoundingClientRect().height
      || popover.getBoundingClientRect().height;

    if (!popoverHeight) {
      return;
    }

    const pseudoStyle = window.getComputedStyle(popover, "::before");
    const beakSize = parseFloat(pseudoStyle.height) || 11;

    // Position popover near the trigger center and clamp within viewport bounds.
    const detailRect = detailRow.getBoundingClientRect();
    const idealTop = triggerCenter - popoverHeight * 2 / 3;
    const clampedTop = clamp(idealTop, GUTTER, viewportHeight - GUTTER - popoverHeight);
    const popoverBottom = clampedTop + popoverHeight;
    const bottomOffset = detailRect.bottom - popoverBottom;
    const beakBottom = clamp(
      popoverBottom - triggerCenter - beakSize / 2,
      DESKTOP_MIN_BEAK_EDGE,
      popoverHeight - DESKTOP_MIN_BEAK_EDGE
    );

    popover.style.setProperty(
      "--technical-popover-desktop-bottom",
      `${bottomOffset}px`
    );
    popover.style.setProperty(
      "--technical-popover-desktop-beak-bottom",
      `${beakBottom}px`
    );
  }

  // Popovers marked as "fixed" (comparison table label cells, product finder questions)
  // are not inside a `.technical-detail` row, so they are positioned with viewport
  // coordinates instead.
  function isFixedPopover(popover) {
    return popover?.dataset.technicalPopoverAnchor === "fixed";
  }

  function getContentElement(popover) {
    return popover.shadowRoot?.querySelector('[part="content"]') || popover;
  }

  function setFixedPosition(popover, trigger) {
    if (!popover || !trigger) {
      return;
    }

    const triggerRect = trigger.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    if (mobileQuery.matches) {
      popover.style.setProperty(
        "--technical-popover-fixed-width",
        `${viewportWidth - GUTTER * 2}px`
      );

      const cardHeight = getContentElement(popover).getBoundingClientRect().height
        || Math.min(viewportHeight - GUTTER * 2, 520);
      const aboveTop = triggerRect.top - MOBILE_VERTICAL_OFFSET - cardHeight;
      const belowTop = triggerRect.bottom + MOBILE_VERTICAL_OFFSET;
      // Prefer above the trigger; drop below it only when that fits and above does not.
      const placeBelow = aboveTop < GUTTER
        && belowTop + cardHeight <= viewportHeight - GUTTER;
      const top = clamp(
        placeBelow ? belowTop : aboveTop,
        GUTTER,
        Math.max(GUTTER, viewportHeight - GUTTER - cardHeight)
      );

      popover.style.setProperty("--technical-popover-fixed-left", `${GUTTER}px`);
      popover.style.setProperty("--technical-popover-fixed-top", `${top}px`);
      return;
    }

    popover.style.removeProperty("--technical-popover-fixed-width");

    const contentRect = getContentElement(popover).getBoundingClientRect();
    const cardWidth = contentRect.width;
    const cardHeight = contentRect.height;

    if (!cardHeight) {
      return;
    }

    const triggerCenter = triggerRect.top + triggerRect.height / 2;
    const rightOfTrigger = triggerRect.right + FIXED_TRIGGER_GAP;
    const leftOfTrigger = triggerRect.left - FIXED_TRIGGER_GAP - cardWidth;
    // Prefer the right side; flip to the left when the card would otherwise cover the trigger.
    const placeLeft = rightOfTrigger + cardWidth > viewportWidth - GUTTER
      && leftOfTrigger >= GUTTER;
    const left = placeLeft
      ? leftOfTrigger
      : clamp(
        rightOfTrigger,
        GUTTER,
        Math.max(GUTTER, viewportWidth - GUTTER - cardWidth)
      );
    const top = clamp(
      triggerCenter - cardHeight / 3,
      GUTTER,
      Math.max(GUTTER, viewportHeight - GUTTER - cardHeight)
    );
    const beakTop = clamp(
      triggerCenter - BEAK_SIZE / 2,
      top + DESKTOP_MIN_BEAK_EDGE,
      top + cardHeight - DESKTOP_MIN_BEAK_EDGE
    );

    popover.style.setProperty("--technical-popover-fixed-left", `${left}px`);
    popover.style.setProperty("--technical-popover-fixed-top", `${top}px`);
    popover.style.setProperty(
      "--technical-popover-fixed-beak-left",
      `${(placeLeft ? left + cardWidth : left) - BEAK_SIZE / 2}px`
    );
    popover.style.setProperty("--technical-popover-fixed-beak-top", `${beakTop}px`);
  }

  function clearFixedPosition(popover) {
    if (!popover) {
      return;
    }

    popover.style.removeProperty("--technical-popover-fixed-width");
    popover.style.removeProperty("--technical-popover-fixed-left");
    popover.style.removeProperty("--technical-popover-fixed-top");
    popover.style.removeProperty("--technical-popover-fixed-beak-left");
    popover.style.removeProperty("--technical-popover-fixed-beak-top");
  }

  function repositionOpenPopovers() {
    document.querySelectorAll(".technical-detail__popover[open]").forEach((popover) => {
      const trigger = popover._technicalTrigger || findTrigger(popover);

      if (isFixedPopover(popover)) {
        setFixedPosition(popover, trigger);
        return;
      }

      if (mobileQuery.matches) {
        clearDesktopPosition(popover);
        setMobilePosition(popover, trigger);
      } else {
        clearMobilePosition(popover);
        setDesktopPosition(popover, trigger);
      }
    });
  }

  function clearMobilePosition(popover) {
    if (!popover) {
      return;
    }

    popover.style.removeProperty("--technical-popover-mobile-width");
    popover.style.removeProperty("--technical-popover-mobile-left");
    popover.style.removeProperty("--technical-popover-mobile-top");
    popover.style.removeProperty("--technical-popover-mobile-height");
  }

  function clearDesktopPosition(popover) {
    if (!popover) {
      return;
    }

    popover.style.removeProperty("--technical-popover-desktop-bottom");
    popover.style.removeProperty("--technical-popover-desktop-beak-bottom");
  }

  document.addEventListener("click", (event) => {
    const trigger = event.target.closest(
      ".technical-detail__info-button[aria-controls]"
    );

    if (trigger) {
      const popover = document.getElementById(trigger.getAttribute("aria-controls"));

      if (popover && popover.classList.contains("technical-detail__popover")) {
        popover._technicalTrigger = trigger;
        closeAllPopovers(popover);
      }

      return;
    }

    closePopoversOutside(event.target);
  });

  function closePopoversOutside(target) {
    document.querySelectorAll(".technical-detail__popover[open]").forEach((popover) => {
      if (isInsidePopover(popover, target)) {
        return;
      }

      popover.hide();
    });
  }

  function isInsidePopover(popover, target) {
    if (popover.contains(target)) {
      return true;
    }

    const content = popover.shadowRoot?.querySelector('[part="content"]');

    if (content && content.contains(target)) {
      return true;
    }

    // Check if the click was on the shadow DOM close button.
    const closeBtn = popover.shadowRoot?.querySelector('[part~="close-button"]');

    if (closeBtn && (closeBtn === target || closeBtn.contains(target))) {
      return true;
    }

    return false;
  }

  // Use mousedown for faster outside-click detection (fires before focus-trap can interfere).
  document.addEventListener("mousedown", (event) => {
    const trigger = event.target.closest(
      ".technical-detail__info-button[aria-controls]"
    );

    if (trigger) {
      return;
    }

    closePopoversOutside(event.target);
  });

  function bindPopoverEvents(popover) {
    if (popover.dataset.technicalPopoverBound === "true") {
      return;
    }

    popover.dataset.technicalPopoverBound = "true";

    popover.addEventListener("dialog:before-show", () => {
      closeAllPopovers(popover);
      const trigger = popover._technicalTrigger || findTrigger(popover);

      if (isFixedPopover(popover)) {
        setFixedPosition(popover, trigger);
        return;
      }

      setMobilePosition(popover, trigger);
      setDesktopPosition(popover, trigger);
    });

    popover.addEventListener("dialog:after-show", () => {
      const trigger = popover._technicalTrigger || findTrigger(popover);

      if (mobileQuery.matches || isFixedPopover(popover)) {
        // Wait for the browser to layout the newly visible popover
        // so we get its actual height instead of 0 or fallback.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (isFixedPopover(popover)) {
              setFixedPosition(popover, trigger);
            } else {
              setMobilePosition(popover, trigger);
            }
          });
        });
      }
    });

    popover.addEventListener("dialog:after-hide", () => {
      clearMobilePosition(popover);
      clearDesktopPosition(popover);
      clearFixedPosition(popover);
    });
  }

  function closeAllPopovers(exceptPopover = null) {
    document.querySelectorAll(".technical-detail__popover[open]").forEach((popover) => {
      if (exceptPopover && popover === exceptPopover) {
        return;
      }

      popover.hide();
    });
  }

  function blockItemHasContent(blockItem) {
    return blockItem.childElementCount > 0 || blockItem.textContent.trim() !== "";
  }

  function syncTechnicalGroupVisibility(scope = document) {
    scope.querySelectorAll(TECHNICAL_GROUP_SELECTOR).forEach((group) => {
      const hasContent = Array.from(group.children).some((child) => {
        return child.classList.contains("product-info__block-item")
          && blockItemHasContent(child);
      });

      group.hidden = !hasContent;
    });
  }

  function initPopovers(scope = document) {
    scope.querySelectorAll(".technical-detail__popover").forEach(bindPopoverEvents);
  }

  function initTechnicalDetails(scope = document) {
    initPopovers(scope);
    syncTechnicalGroupVisibility(scope);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initTechnicalDetails();
    });
  } else {
    initTechnicalDetails();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initTechnicalDetails(event.target);
  });

  // Close open popovers and reset positions when crossing the mobile/desktop breakpoint.
  mobileQuery.addEventListener("change", () => {
    closeAllPopovers();

    document.querySelectorAll(".technical-detail__popover").forEach((popover) => {
      clearMobilePosition(popover);
      clearDesktopPosition(popover);
      clearFixedPosition(popover);
    });
  });

  window.addEventListener("resize", repositionOpenPopovers);

  // Viewport-anchored popovers have to follow their trigger while the page or the
  // comparison table scroller moves underneath them.
  let scrollFrame = null;

  document.addEventListener("scroll", () => {
    if (scrollFrame || !document.querySelector('.technical-detail__popover[open][data-technical-popover-anchor="fixed"]')) {
      return;
    }

    scrollFrame = requestAnimationFrame(() => {
      scrollFrame = null;
      repositionOpenPopovers();
    });
  }, { capture: true, passive: true });
})();
